import React, { useState } from 'react';
import { forNamespace } from 'app/common/lib/util/i18n';
import classNames from 'classnames';
import styles from './BoardVisibilityRestrictionAlert.less';
import {
  Board_Prefs_PermissionLevel,
  useBoardVisibilityRestrictionAlertQuery,
} from '@trello/graphql/generated';
import { Spinner } from 'app/src/components/Spinner';
import Icon, { IconColor, IconName } from 'app/common/components/controls/icon';
import { useRestrictedBoardVisibility } from './useRestrictedBoardVisibility';
import RouterLink from 'app/common/components/singlespa/router-link';
import { memberId } from '@trello/session-cookie';

const noop = () => {};

const format = forNamespace('workspaces preamble');
const permissionsFormat = forNamespace('board perms');

interface BoardVisibilityRestrictionAlertProps {
  boardId: string;
  onSubmit: (visibility: Board_Prefs_PermissionLevel) => void;
  orgId: string;
}

export const BoardVisibilityRestrictionAlert: React.FunctionComponent<BoardVisibilityRestrictionAlertProps> = ({
  boardId,
  onSubmit,
  orgId,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { data, loading } = useBoardVisibilityRestrictionAlertQuery({
    variables: { orgId },
  });

  const {
    boardVisibility,
    loading: boardVisibilityRestrictionsLoading,
    teamAllowsPrivateBoards,
    teamAllowsPublicBoards,
    teamAllowsTeamVisibleBoards,
    teamRestrictsAllBoardVisibilities,
  } = useRestrictedBoardVisibility({
    orgId,
    boardId,
  });

  if (loading || boardVisibilityRestrictionsLoading) {
    return <Spinner centered />;
  }

  // It is possible that a BC team's settings are such that all board visibilities are restricted for some or all team members
  if (teamRestrictsAllBoardVisibilities) {
    const isTeamAdmin = !!data?.organization?.memberships?.find(
      (membership) =>
        membership.idMember === memberId &&
        membership.memberType === 'admin' &&
        !membership.deactivated &&
        !membership.unconfirmed,
    );
    const orgName = (
      <strong key="orgName">{data?.organization?.displayName}</strong>
    );
    const messaging = isTeamAdmin
      ? format('board-visibility-all-restricted', {
          orgName,
          teamSettingsPageLink: (
            <RouterLink
              key="settingsLink"
              href={`/${data?.organization?.name}/account`}
            >
              {format('board-visibility-settings-link')}
            </RouterLink>
          ),
        })
      : format('board-visibility-all-restricted-not-admin', {
          orgName,
        });

    return <p id="allVisibilitiesAreRestrictedMessaging">{messaging}</p>;
  }

  const onClickOption = (visibility: Board_Prefs_PermissionLevel) => {
    setIsSubmitted(true);
    onSubmit(visibility);
  };

  return (
    <>
      <p>
        {format('board-visibility-messaging', {
          orgName: (
            <strong key="orgName">{data?.organization?.displayName}</strong>
          ),
        })}
      </p>
      <div
        id="privateVisibilityOption"
        role="button"
        onClick={
          teamAllowsPrivateBoards && !isSubmitted
            ? () => onClickOption(Board_Prefs_PermissionLevel.Private)
            : noop
        }
        className={classNames(styles.boardVisibilityOption, {
          [styles.boardVisibilityOptionDisabled]:
            !teamAllowsPrivateBoards || isSubmitted,
        })}
      >
        <div className={styles.boardVisibilityOptionTitle}>
          <Icon
            className={styles.boardVisibilityIcon}
            color={IconColor.Red}
            name={IconName.Private}
            small
          />
          {permissionsFormat(['private', 'name'])}
          {boardVisibility === 'private' && (
            <Icon
              className={styles.boardVisibilityIcon}
              name={IconName.Check}
              small
            />
          )}
        </div>
        <p className={styles.boardVisibilityOptionDescription}>
          {permissionsFormat(['private', 'short summary'])}
          &nbsp;
          {!teamAllowsPrivateBoards && (
            <span className={styles.boardVisibilityDisabledMessaging}>
              {format('board-visibility-option-disabled')}
            </span>
          )}
        </p>
      </div>
      <div
        id="teamVisibleVisibilityOption"
        role="button"
        onClick={
          teamAllowsTeamVisibleBoards && !isSubmitted
            ? () => onClickOption(Board_Prefs_PermissionLevel.Org)
            : noop
        }
        className={classNames(styles.boardVisibilityOption, {
          [styles.boardVisibilityOptionDisabled]:
            !teamAllowsTeamVisibleBoards || isSubmitted,
        })}
      >
        <div className={styles.boardVisibilityOptionTitle}>
          <Icon
            className={styles.boardVisibilityIcon}
            name={IconName.Organization}
            small
          />
          {permissionsFormat(['org', 'name'])}
          {boardVisibility === 'org' && (
            <Icon
              className={styles.boardVisibilityIcon}
              name={IconName.Check}
              small
            />
          )}
        </div>
        <p className={styles.boardVisibilityOptionDescription}>
          {permissionsFormat(['org', 'short summary'])}
          &nbsp;
          {!teamAllowsTeamVisibleBoards && (
            <span className={styles.boardVisibilityDisabledMessaging}>
              {format('board-visibility-option-disabled')}
            </span>
          )}
        </p>
      </div>
      <div
        id="publicVisibilityOption"
        role="button"
        onClick={
          teamAllowsPublicBoards && !isSubmitted
            ? () => onClickOption(Board_Prefs_PermissionLevel.Public)
            : noop
        }
        className={classNames(styles.boardVisibilityOption, {
          [styles.boardVisibilityOptionDisabled]:
            !teamAllowsPublicBoards || isSubmitted,
        })}
      >
        <div className={styles.boardVisibilityOptionTitle}>
          <Icon
            className={styles.boardVisibilityIcon}
            color={IconColor.Green}
            name={IconName.Public}
            small
          />
          {permissionsFormat(['public', 'name'])}
          {boardVisibility === 'public' && (
            <Icon
              className={styles.boardVisibilityIcon}
              name={IconName.Check}
              small
            />
          )}
        </div>
        <p className={styles.boardVisibilityOptionDescription}>
          {permissionsFormat(['public', 'short summary'])}
          &nbsp;
          {!teamAllowsPublicBoards && (
            <span className={styles.boardVisibilityDisabledMessaging}>
              {format('board-visibility-option-disabled')}
            </span>
          )}
        </p>
      </div>
    </>
  );
};
